import { useContext } from 'react';
import { View } from '@shared/components/View';
import { MBSelectableDot } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBScrollView } from '@shared/components/scrollview';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import {
  MBTextBody1,
  MBTextBody2,
  MBTextBody2Bold,
  MBTextOverlineBold,
} from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { Divider } from '../../divider';
import { DefaultRelatedCardSelectionHooks } from './hooks/DefaultRelatedCardSelectionHooks';

interface Props {
  relatedCardDefaultHooks: DefaultRelatedCardSelectionHooks;
}

export function DefaultRelatedCardSelection({ relatedCardDefaultHooks }: Props) {
  const colors = useContext(ColorThemeContext);

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={3} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('workflowTemplate.responseOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingHorizontal={3} paddingBottom={2}>
        <MBSearchBar
          value={relatedCardDefaultHooks.keyword}
          onChangeText={relatedCardDefaultHooks.onSearch}
          placeholder={i18n('common.search')}
        />
      </View>
      <MBScrollView style={{ maxHeight: grid(30) }}>
        {relatedCardDefaultHooks.loading && <MBLoadingIndicator style={{ height: grid(30) }} />}
        {!relatedCardDefaultHooks.loading && (
          <>
            {relatedCardDefaultHooks.keyword.length === 0 &&
              relatedCardDefaultHooks.selectedCards.length > 0 && (
                <>
                  <View paddingHorizontal={2} row>
                    <MBTextBody2Bold>
                      {relatedCardDefaultHooks.selectedCards.length + ' '}
                    </MBTextBody2Bold>
                    <MBTextBody2>{i18n('common.selected')}</MBTextBody2>
                  </View>
                  {relatedCardDefaultHooks.selectedCards.map(card => {
                    return (
                      <DefaultRelatedCardSelectionRow
                        key={card.id}
                        name={card.name}
                        isSelected={card.isSelected}
                        onPress={() => relatedCardDefaultHooks.onToggleDefault(card.id)}
                      />
                    );
                  })}
                  <View paddingTop={0.5}>
                    <Divider />
                  </View>
                </>
              )}
            {relatedCardDefaultHooks.keyword.length === 0 &&
              relatedCardDefaultHooks.cardsHasMore && (
                <View paddingHorizontal={2} paddingVertical={1}>
                  <MBTextBody2Bold>{i18n('builder.defaultCard.maxCardListHelper')}</MBTextBody2Bold>
                </View>
              )}
            {relatedCardDefaultHooks.cards.map(card => {
              return (
                <DefaultRelatedCardSelectionRow
                  key={card.id}
                  name={card.name}
                  isSelected={card.isSelected}
                  onPress={() => relatedCardDefaultHooks.onToggleDefault(card.id)}
                />
              );
            })}
          </>
        )}
      </MBScrollView>
    </View>
  );
}

interface DefaultRelatedCardSelectionRowProps {
  name: string;
  isSelected: boolean;
  onPress();
}

function DefaultRelatedCardSelectionRow({
  name,
  isSelected,
  onPress,
}: DefaultRelatedCardSelectionRowProps) {
  return (
    <MBTouchableOpacity onPress={onPress}>
      <View flex row centerVertically paddingVertical={0.25} paddingHorizontal={3}>
        <View wrap flex={4} style={{ alignContent: 'flex-start' }}>
          <MBTextBody1>{name}</MBTextBody1>
        </View>
        <View flex style={{ alignItems: 'flex-end' }}>
          <MBSelectableDot selected={isSelected} radio={false} />
        </View>
      </View>
    </MBTouchableOpacity>
  );
}
